import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Flag, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { REPORT_REASONS } from '@/data/taxonomy';
import { reportService } from '@/services/api';
import { contactService } from '@/services/contacts';
import { useData } from '@/hooks/useData';
import { formatPhone, onlyDigits } from '@/lib/format';
import { FieldError } from './Primitives';

export const ReportDialog = ({ targetType, targetId, testId = 'report-button' }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) return setErr('Selecione um motivo.');
    setBusy(true);
    try {
      await reportService.create({ targetType, targetId, reason, details });
      toast.success('Denúncia enviada', { description: 'A moderação vai analisar o caso.' });
      setOpen(false);
      setReason('');
      setDetails('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
    return null;
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setErr(''); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="text-muted-foreground hover:bg-[rgba(255,59,92,0.08)] hover:text-[rgb(255,130,150)]" data-testid={testId}>
          <Flag /> Denunciar
        </Button>
      </DialogTrigger>
      <DialogContent className="border-white/10 bg-[#0b0f16] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Denunciar publicação</DialogTitle>
          <DialogDescription>Denúncias são analisadas pela moderação conforme as diretrizes da comunidade. Seus dados não são exibidos ao denunciado.</DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={(v) => { setReason(v); setErr(''); }} className="grid gap-2 sm:grid-cols-2" data-testid="report-reason-group">
          {REPORT_REASONS.map((r) => (
            <Label key={r.id} htmlFor={`rr-${r.id}`} className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-normal hover:border-white/20">
              <RadioGroupItem id={`rr-${r.id}`} value={r.id} data-testid={`report-reason-${r.id}`} /> {r.name}
            </Label>
          ))}
        </RadioGroup>
        <FieldError msg={err} />
        <div>
          <Label htmlFor="report-details" className="text-sm">Detalhes (opcional)</Label>
          <Textarea id="report-details" maxLength={600} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Descreva o problema" className="mt-1.5" data-testid="report-details-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="report-submit-button">
            {busy ? <Loader2 className="animate-spin" /> : <Flag />} Enviar denúncia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DialogGate = ({ testId, title, text, children }) => (
  <div className="space-y-4 py-2 text-center" data-testid={testId}>
    <p className="text-sm font-medium">{title}</p>
    <p className="text-sm text-muted-foreground">{text}</p>
    <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">{children}</div>
  </div>
);

/** Pedido de acesso aos contatos da empresa. Só usuários logados com empresa cadastrada podem pedir. */
export const ContactDialog = ({ company, opportunity, trigger, testId = 'contact-button' }) => {
  const { user, myCompany, authLoading } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ message: '', whatsapp: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const isExample = !!company?.isDemo;
  const isOwn = !!myCompany && myCompany.id === company?.id;

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.message.trim().length < 15) er.message = 'Escreva ao menos 15 caracteres.';
    if (form.whatsapp && ![10, 11].includes(onlyDigits(form.whatsapp).length)) er.whatsapp = 'Informe DDD + número (10 ou 11 dígitos).';
    setErrors(er);
    if (Object.keys(er).length) return;

    const parts = [];
    if (opportunity) parts.push(`Sobre a oportunidade: ${opportunity.title}`);
    parts.push(form.message.trim());
    if (form.whatsapp) parts.push(`WhatsApp para retorno: ${formatPhone(form.whatsapp)}`);

    setBusy(true);
    try {
      await contactService.request(company.id, parts.join('\n'));
      toast.success('Pedido enviado', { description: `${company.name} vai analisar seu pedido na caixa de entrada.` });
      setOpen(false);
      setForm({ message: '', whatsapp: '' });
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  const renderBody = () => {
    if (isExample) {
      return <DialogGate testId="contact-gate-example" title="Empresa de exemplo" text="Esta empresa é um exemplo da plataforma e não recebe pedidos de contato." />;
    }
    if (authLoading) {
      return <p className="py-6 text-center text-sm text-muted-foreground">Carregando…</p>;
    }
    if (!user) {
      return (
        <DialogGate testId="contact-gate-login" title="Entre para pedir contato" text="Para pedir acesso aos contatos de outra empresa, você precisa ter uma conta e uma empresa cadastrada na rede.">
          <Button asChild className="btn-glow"><Link to="/entrar">Entrar</Link></Button>
          <Button asChild variant="secondary" className="border border-white/10"><Link to="/criar-conta">Criar conta</Link></Button>
        </DialogGate>
      );
    }
    if (!myCompany) {
      return (
        <DialogGate testId="contact-gate-company" title="Cadastre sua empresa" text="Só empresas cadastradas podem pedir contato. O cadastro é gratuito.">
          <Button asChild className="btn-glow"><Link to="/cadastrar-empresa">Cadastrar empresa</Link></Button>
        </DialogGate>
      );
    }
    if (isOwn) {
      return (
        <DialogGate testId="contact-gate-own" title="Esta é a sua empresa" text="Os pedidos recebidos ficam na caixa de entrada do painel.">
          <Button asChild variant="secondary" className="border border-white/10"><Link to="/painel/mensagens">Abrir caixa de entrada</Link></Button>
        </DialogGate>
      );
    }
    return (
      <>
        <p className="text-sm text-muted-foreground">
          Você pede acesso aos contatos desta empresa. Se ela aceitar, você verá o WhatsApp, o e-mail e o CNPJ dela. A empresa verá o seu nome e o seu e-mail.
        </p>
        <form onSubmit={submit} className="space-y-3" noValidate data-testid="contact-form">
          <div>
            <Label htmlFor="ct-message">Mensagem</Label>
            <Textarea id="ct-message" rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="mt-1.5" aria-invalid={!!errors.message} data-testid="contact-message-input" />
            <FieldError msg={errors.message} />
          </div>
          <div>
            <Label htmlFor="ct-whatsapp">WhatsApp para retorno (opcional)</Label>
            <Input id="ct-whatsapp" inputMode="tel" value={formatPhone(form.whatsapp)} onChange={(e) => setForm((f) => ({ ...f, whatsapp: onlyDigits(e.target.value) }))} placeholder="(00) 00000-0000" className="mt-1.5" aria-invalid={!!errors.whatsapp} data-testid="contact-whatsapp-input" />
            <FieldError msg={errors.whatsapp} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="btn-glow" data-testid="contact-submit-button">
              {busy ? <Loader2 className="animate-spin" /> : <Send />} Enviar pedido
            </Button>
          </DialogFooter>
        </form>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="btn-glow" data-testid={testId}>
            <Send /> Entrar em contato
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="border-white/10 bg-[#0b0f16] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Contato com {company?.name}</DialogTitle>
          <DialogDescription>
            {opportunity ? <>Sobre: <span className="text-foreground">{opportunity.title}</span>. </> : null}
            Pedido de acesso aos contatos da empresa.
          </DialogDescription>
        </DialogHeader>
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
};